import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PublicNavbar from '../components/layout/PublicNavbar';

// App version for tracking deployments
const APP_VERSION = 'v1.0.1';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isAutoLoginAttempted, setIsAutoLoginAttempted] = useState(false);

  // Check for existing login without forcing logout
  useEffect(() => {
    console.log(`LoginPage mounted - App version: ${APP_VERSION}`);
    console.log('Checking for existing login session, user:', user?.email || 'none');
    
    // If user is already logged in, redirect to dashboard
    if (user) {
      console.log('User already logged in, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
    } else {
      console.log('No active session found, showing login form');
    }
    
    setIsAutoLoginAttempted(true);
  }, [user, navigate]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Log the login attempt for debugging
    console.log('Attempting login with email:', email);
    console.log('App version:', APP_VERSION);

    try {
      console.log('Sending login request to:', `${api.defaults.baseURL}/auth/login`);
      
      const response = await api.post('/auth/login', {
        email,
        password
      });

      console.log('Login response received:', response.status);
      
      // Detailed logging (excluding sensitive data)
      console.log('Login response headers:', response.headers);
      console.log('Login response data structure:', Object.keys(response.data));
      
      const { token, user } = response.data;
      
      // Validate user object has expected structure
      if (!user || !user.id || !user.email || !user.name) {
        throw new Error('Invalid user data received from server');
      }
      
      console.log('Login successful, user role:', user.role || 'undefined');
      
      // Ensure permissions exists
      if (!user.permissions) {
        console.warn('Server did not provide permissions, initializing as empty array');
        user.permissions = [];
      }
      
      // Special debugging for santosh@peachmode.com user
      if (user.email === 'santosh@peachmode.com') {
        console.log('%c Special Debug for Santosh User', 'background: #4CAF50; color: white; padding: 4px; border-radius: 4px;');
        console.log('User details:', { 
          name: user.name, 
          role: user.role,
          permissions: user.permissions 
        });
        
        // If there are permissions, display them more clearly
        if (user.permissions && user.permissions.length > 0) {
          const permissionDetails = Array.isArray(user.permissions) ? 
            user.permissions.map((p: any) => {
              if (typeof p === 'string') {
                return { module: p, type: 'string' };
              } else if (typeof p === 'object' && p !== null) {
                return { 
                  module: p.module,
                  type: 'object', 
                  view: p.can_view ? '✅' : '❌',
                  add: p.can_add ? '✅' : '❌', 
                  edit: p.can_edit ? '✅' : '❌', 
                  delete: p.can_delete ? '✅' : '❌'
                };
              }
              return { type: 'unknown' };
            }) : [];
            
          console.table(permissionDetails);
          
          // List menu items that should be accessible
          const menuModules = ['workers', 'design-cutting', 'assignments', 'payments', 'dispatch', 'expenses', 'vendors'];
          console.log('Menu visibility prediction:');
          
          menuModules.forEach(module => {
            const normalized = module.toLowerCase().replace(/[-_]/g, '_');
            
            let shouldShow = false;
            if (user.role === 'MASTER') {
              shouldShow = true;
            } else if (Array.isArray(user.permissions)) {
              if (typeof user.permissions[0] === 'string') {
                // String array format
                shouldShow = user.permissions.some((p: string) => {
                  const normalizedP = p.toLowerCase().replace(/[-_]/g, '_');
                  return normalizedP === normalized;
                });
              } else if (typeof user.permissions[0] === 'object' && user.permissions[0] !== null) {
                // Object format
                const perm = user.permissions.find((p: any) => {
                  const normalizedModule = p.module.toLowerCase().replace(/[-_]/g, '_');
                  return normalizedModule === normalized;
                });
                shouldShow = perm ? perm.can_view : false;
              }
            }
            
            console.log(`${module}: ${shouldShow ? '✅ Should show' : '❌ Should not show'}`);
          });
        }
      }
      
      // Store token and user data manually first for debugging
      try {
        console.log('Manually storing token and user data in localStorage');
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        console.log('localStorage check after setting:');
        console.log('token present:', !!localStorage.getItem('token'));
        console.log('user present:', !!localStorage.getItem('user'));
      } catch (storageError) {
        console.error('Error storing data in localStorage:', storageError);
      }
      
      // Then use the login function
      login(token);
      
      console.log('Login function completed, redirecting to dashboard');
      // Always redirect to dashboard after successful login
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      console.error('Login error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message
      });
      
      // Enhanced error message with more details
      let errorMessage = 'Login failed';
      
      if (err.response?.status === 401) {
        errorMessage = 'Invalid email or password. Please try again.';
      } else if (err.response?.data?.error) {
        errorMessage = `Server error: ${err.response.data.error}`;
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const baseUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:9000/api/v1';
    console.log('Redirecting to Google login:', `${baseUrl}/auth/google`);
    window.location.href = `${baseUrl}/auth/google`;
  };

  if (!isAutoLoginAttempted) {
    // Show loading spinner while checking for existing session
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh'
        }}
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Preparing login page...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <PublicNavbar />
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%'
            }}
          >
            <Typography component="h1" variant="h5" gutterBottom>
              Sign in to Factory App
            </Typography>
            
            <Typography variant="caption" color="textSecondary" sx={{ mb: 2 }}>
              {APP_VERSION}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleEmailLogin} sx={{ width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>
            </Box>

            <Divider sx={{ width: '100%', my: 2 }}>OR</Divider>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              Sign in with Google
            </Button>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default LoginPage; 